import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Container from '../components/layout/Container';
import { fetchNews } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';

// Loading Skeleton Component
const NewsSkeleton = () => (
  <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg animate-pulse border border-white/20 overflow-hidden">
    <div className="bg-gradient-to-br from-slate-200 to-slate-300 h-48 lg:h-56"></div>
    <div className="p-6 space-y-4">
      <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-1/4"></div>
      <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-6 rounded-full w-3/4"></div>
      <div className="space-y-2">
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-full"></div>
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-2/3"></div>
      </div>
      <div className="flex justify-between items-center pt-2">
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-1/4"></div>
        <div className="bg-gradient-to-r from-slate-200 to-slate-300 h-4 rounded-full w-1/5"></div>
      </div>
    </div>
  </div>
);

// News Card Component
const NewsCard = ({ item, formatDate, index, t }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Safe navigation handler
  const handleCardClick = useCallback((e) => {
    // Prevent navigation if clicking on interactive elements
    if (e.target.tagName === 'A' || e.target.closest('a')) {
      return;
    }
    window.location.href = `/news/${item.id}`;
  }, [item.id]);

  return (
    <div 
      className="group bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border border-white/30 cursor-pointer"
      style={{ 
        animationDelay: `${index * 0.1}s`,
        animation: 'fadeInUp 0.6s ease-out forwards'
      }}
      onClick={handleCardClick}
    >
      {/* Image Section */}
      {item.image && (
        <div className="relative overflow-hidden h-48 lg:h-56">
          {/* Image Loading Placeholder */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
              <div className="w-8 h-8 border-3 border-slate-300 border-t-red-500 rounded-full animate-spin"></div>
            </div>
          )}
          
          <img 
            src={item.image} 
            alt={item.title}
            className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          
          {/* Overlay Effects */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          
          {/* Featured Badge */}
          {item.is_featured && (
            <div className="absolute top-4 left-4">
              <div className="relative">
                <span className="bg-gradient-to-r from-red-500 via-pink-500 to-red-500 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg backdrop-blur-sm animate-pulse">
                  ⭐ {t('pages.news.featured') || 'Asosiy Yangilik'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-pink-400 rounded-full blur opacity-50"></div>
              </div>
            </div>
          )}

          {/* Read Time Badge */}
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-slate-700 text-sm font-semibold px-3 py-1 rounded-full shadow-md">
            📖 {t('pages.news.readTime') || '2 daqiqa'}
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="p-6 space-y-4">
        {/* Title */}
        <h3 className="text-xl font-bold text-slate-900 leading-tight line-clamp-2 group-hover:text-red-600 transition-colors duration-300">
          <Link to={`/news/${item.id}`} className="hover:text-red-600" onClick={(e) => e.stopPropagation()}>
            {item.title || t('pages.news.noTitle') || 'Sarlavha yo\'q'}
          </Link>
        </h3>

        {/* Content Preview */}
        <p className="text-slate-600 leading-relaxed line-clamp-3 text-sm">
          {item.content || item.summary || t('pages.news.noContent') || 'Matn yo\'q...'}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="flex items-center space-x-2 text-slate-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-medium">
              {formatDate(item.created_at)}
            </span>
          </div>
          
          <Link 
            to={`/news/${item.id}`}
            className="group/link inline-flex items-center text-red-600 hover:text-red-700 font-semibold text-sm transition-all duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="mr-1">{t('common.readMore') || 'Batafsil'}</span>
            <svg className="w-4 h-4 transform group-hover/link:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ t }) => (
  <div className="text-center py-20">
    <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-16 max-w-lg mx-auto border border-slate-200/50 shadow-xl">
      <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-8">
        <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold text-slate-800 mb-4">{t('pages.news.noNews') || 'Yangiliklar mavjud emas'}</h3>
      <p className="text-slate-600 mb-8 leading-relaxed">
        {t('pages.news.noNewsDescription') || "Hozircha yangiliklar yo'q. Tez orada yangi ma'lumotlar bilan qaytamiz!"}
      </p>
      <Link 
        to="/"
        className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
      >
        <span className="mr-2">{t('common.backHome') || 'Bosh sahifaga qaytish'}</span>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      </Link>
    </div>
  </div>
);

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, t }) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= Math.min(5, totalPages); i++) {
          pages.push(i);
        }
        if (totalPages > 5) {
          pages.push('...');
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        if (totalPages > 5) {
          pages.push('...');
        }
        for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex justify-center items-center mt-12">
      <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/30 p-4">
        <div className="flex items-center gap-2">
          {/* Previous Button */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              currentPage === 1
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-xl transform hover:scale-105'
            }`}
          >
            {t('common.previous') || 'Oldingi'}
          </button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1 mx-4">
            {pageNumbers.map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === 'number' ? onPageChange(page) : null}
                disabled={typeof page !== 'number'}
                className={`min-w-[40px] h-10 rounded-xl font-semibold transition-all duration-200 ${
                  page === currentPage
                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg'
                    : typeof page === 'number'
                    ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900 transform hover:scale-105'
                    : 'bg-transparent text-slate-400 cursor-default'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          {/* Next Button */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              currentPage === totalPages
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-xl transform hover:scale-105'
            }`}
          >
            {t('common.next') || 'Keyingi'}
          </button>
        </div>
      </div>
    </div>
  );
};

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [newsPerPage] = useState(9);
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const loadNews = async () => {
      try {
        setLoading(true);
        setError('');
        
        const data = await fetchNews(currentLanguage);
        
        if (mounted) {
          setNews(data || []);
        }
      } catch (error) {
        console.error('News loading error:', error);
        if (mounted && !controller.signal.aborted) {
          setError(error.message || 'Yangiliklar yuklanishda xatolik yuz berdi');
          setNews([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadNews();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [currentLanguage]);

  const formatDate = useCallback((dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return dateString;
    }
  }, []);

  // Pagination calculations
  const totalPages = Math.ceil(news.length / newsPerPage);
  const startIndex = (currentPage - 1) * newsPerPage;
  const endIndex = startIndex + newsPerPage;
  const currentNews = news.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <Container className="py-8">
          <div className="py-12 lg:py-20">
            {/* Header Skeleton */}
            <div className="text-center mb-16">
              <div className="inline-block bg-red-100/80 rounded-full px-6 py-3 mb-6">
                <div className="w-24 h-4 bg-red-200 rounded animate-pulse"></div>
              </div>
              <div className="w-80 h-12 bg-slate-200 rounded mx-auto mb-4 animate-pulse"></div>
              <div className="w-96 h-6 bg-slate-100 rounded mx-auto animate-pulse"></div>
            </div>
            
            {/* News Grid Skeleton */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {Array.from({ length: 9 }, (_, i) => (
                <NewsSkeleton key={i} />
              ))}
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <Container className="py-8">
          <div className="text-center py-20">
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-12 max-w-lg mx-auto border border-red-200/50 shadow-xl">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-red-700 mb-4">{t('common.error') || 'Xatolik yuz berdi'}</h3>
              <p className="text-red-600 mb-8">{error}</p>
              <div className="flex gap-4 justify-center">
                <button 
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span className="mr-2">{t('common.reload') || 'Qayta yuklash'}</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <Link 
                  to="/"
                  className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span className="mr-2">{t('common.backHome') || 'Bosh sahifa'}</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <Container className="py-8">
        <div className="py-12 lg:py-20">
          {/* Page Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-slate-900 via-red-900 to-slate-900 bg-clip-text text-transparent leading-tight">
              {t('pages.news.title') || 'Yangiliklar'}
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
              {t('pages.news.subtitle') || 'Kompaniyamiz faoliyati va sohada sodir bo\'layotgan yangiliklar haqida ma\'lumot oling.'}
            </p>
            <div className="w-20 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full"></div>
          </div>

          {/* News Grid */}
          {news.length === 0 ? (
            <EmptyState t={t} />
          ) : (
            <>
              {/* Stats */}
              <div className="flex justify-center mb-12">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/30 shadow-sm">
                  <span className="font-bold text-slate-900 text-lg">{news.length}</span>
                  <span className="text-slate-600 ml-2">{t('pages.news.available') || 'yangilik mavjud'}</span>
                </div>
              </div>

              {/* News Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {currentNews.map((item, index) => (
                  <NewsCard 
                    key={item.id} 
                    item={item} 
                    formatDate={formatDate}
                    index={index}
                    t={t}
                  />
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                t={t}
              />

              {/* Newsletter CTA */}
              <div className="mt-20 relative bg-gradient-to-br from-red-50/90 via-white/80 to-red-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-red-200/30 overflow-hidden">
                <div className="bg-gradient-to-r from-red-800 via-red-900 to-red-800 rounded-3xl p-12 lg:p-16 text-center text-white overflow-hidden shadow-2xl">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  }}></div>
                </div>
                
                <div className="relative z-10">
                  <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                    {t('pages.news.ctaTitle') || "Yangiliklar haqida birinchi bo'lib xabardor bo'ling"}
                  </h2>
                  <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto leading-relaxed">
                    {t('pages.news.ctaDescription') || "Bizning ijtimoiy tarmoqlarda kuzatib boring va muhim yangilikları o'tkazib yubormang!"}
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <a 
                      href="https://t.me/Bruce_service_uzbekistan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      <span className="mr-2">📱 {t('common.telegram') || 'Telegram'}</span>
                    </a>
                    <a 
                      href="https://www.instagram.com/bruce_uzbekistan_"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      <span className="mr-2">📸 {t('common.instagram') || 'Instagram'}</span>
                    </a>
                  </div>
                </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Container>
      
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        
        .line-clamp-3 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
        }
        
        .border-3 {
          border-width: 3px;
        }
      `}</style>
    </div>
  );
};

export default News;
